<template>
    <validation-observer ref="form" v-slot="{ meta }">
        <modal
            :id="modalId"
            :title="title"
            ok-title="Save"
            :ok-disabled="!meta.valid || loading"
            @ok.prevent="save"
            @hide="hideModal"
            @shown="onModalShown"
        >
            <busy-modal :show="loading"></busy-modal>
            <b-form @submit.prevent="save" v-if="values">
                <b-row>
                    <b-col>
                        <validation-provider name="recurrence.start" v-slot="{ errors }">
                            <form-datepicker
                                v-model="values.recurrence.start"
                                label="Start Date:"
                                :hide-header="true"
                                :display-format="userUnicodeDateFormat"
                                :error="errors[0]"
                            ></form-datepicker>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="recurrence.until" v-slot="{ errors }">
                            <form-datepicker
                                v-model="values.recurrence.until"
                                label="Expire Date:"
                                :hide-header="true"
                                :reset-button="true"
                                :empty-value="null"
                                :display-format="userUnicodeDateFormat"
                                :error="errors[0]"
                            ></form-datepicker>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <label>Repeat every:</label>
                        <div class="recurring-rental-expenses__repeat-every">
                            <validation-provider
                                class="recurring-rental-expenses__repeat-every__interval"
                                name="recurrence.interval"
                                rules="required"
                                v-slot="{ errors }"
                            >
                                <form-input
                                    v-model="values.recurrence.interval"
                                    class="mr-2"
                                    :error="errors[0]"
                                ></form-input>
                            </validation-provider>
                            <validation-provider name="recurrence.freq" rules="required" v-slot="{ errors }">
                                <form-select
                                    v-model="values.recurrence.freq"
                                    :options="freqOptions"
                                    :error="errors[0]"
                                ></form-select>
                            </validation-provider>
                        </div>
                    </b-col>
                </b-row>
                <b-row v-if="isRecurrenceFreqWeekly">
                    <b-col>
                        <validation-provider name="recurrence.byday" rules="required" v-slot="{ errors }">
                            <form-badge-select
                                label="Repeat on:"
                                v-model="values.recurrence.byday"
                                :options="weekdayOptions"
                                :error="errors[0]"
                            ></form-badge-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-else-if="isRecurrenceFreqMonthly">
                    <b-col>
                        <validation-provider name="recurrence.bymonthday" rules="required" v-slot="{ errors }">
                            <form-select
                                v-model="bymonthday"
                                label="Repeat on day:"
                                :options="monthDayOptions"
                                :error="errors[0]"
                            ></form-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-else-if="isRecurrenceFreqYearly">
                    <b-col>
                        <label>Repeat on:</label>
                        <div class="recurring-rental-expenses__yearly-repeat-on">
                            <validation-provider name="recurrence.bymonth" rules="required" v-slot="{ errors }">
                                <form-select
                                    v-model="bymonth"
                                    class="mr-2"
                                    :options="monthOptions"
                                    :error="errors[0]"
                                ></form-select>
                            </validation-provider>
                            <validation-provider
                                name="recurrence.bymonthday"
                                class="recurring-rental-expenses__yearly-repeat-on__bymonthday"
                                rules="required"
                                v-slot="{ errors }"
                            >
                                <form-select
                                    v-model="bymonthday"
                                    :options="yearlyMonthDayOptions"
                                    :error="errors[0]"
                                ></form-select>
                            </validation-provider>
                        </div>
                    </b-col>
                </b-row>
                <hr />
                <b-row>
                    <b-col>
                        <label> Properties: </label>
                        <validation-provider name="properties" v-slot="{ errors }" rules="required">
                            <properties-search-multiselect
                                v-model="values.properties"
                                :is-form="true"
                                :initial-value="initialValues.properties"
                                :properties="properties"
                                :error="errors[0]"
                            ></properties-search-multiselect>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="amount" v-slot="{ errors }" rules="validFloat|required">
                            <form-input label="Amount:" v-model="values.amount" :error="errors[0]"></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <label for="rental-expenses-account-id"> Account: </label>
                        <validation-provider name="account_id" v-slot="{ errors }" :style="{ width: '95%' }">
                            <rental-expense-account-select
                                v-model="values.account_id"
                                :error="errors[0]"
                            ></rental-expense-account-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="assignee" v-slot="{ errors }">
                            <rental-expense-assignee-select
                                v-model="values.assignee"
                                :error="errors[0]"
                            ></rental-expense-assignee-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="vendor_id" v-slot="{ errors }">
                            <rental-expense-vendor-select
                                v-model="values.vendor_id"
                                :error="errors[0]"
                            ></rental-expense-vendor-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="description" rules="required" v-slot="{ errors }">
                            <form-textarea
                                label="Description:"
                                v-model="values.description"
                                :error="errors[0]"
                            ></form-textarea>
                        </validation-provider>
                    </b-col>
                </b-row>
            </b-form>
        </modal>
    </validation-observer>
</template>

<script>
    import { mapGetters } from 'vuex';
    import isEmpty from 'lodash/isEmpty';
    import isNil from 'lodash/isNil';
    import range from 'lodash/range';
    import format from 'date-fns/format';
    import parseISO from 'date-fns/parseISO';
    import API from '../../../api';
    import { formatResponseError } from '../../../utils/api';
    import { getDaysInMonth } from '../../../utils/dates';
    import {
        RECURRENCE_FREQ_YEARLY,
        RECURRENCE_FREQ_MONTHLY,
        RECURRENCE_FREQ_WEEKLY,
        RECURRENCE_FREQ_DAILY,
        RECURRENCE_MONTH_JANUARY,
        RECURRENCE_MONTH_MAPPING,
        RECURRENCE_WEEKDAY_MONDAY,
        RECURRENCE_WEEKDAY_MAPPING,
    } from '../../../constants';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import FormBadgeSelect from '../../generic/form-badge-select/form-badge-select.vue';
    import FormCheckbox from '../../generic/form-checkbox/form-checkbox.vue';
    import FormDatepicker from '../../generic/form-datepicker/form-datepicker.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
    import Modal from '../../generic/modal/modal.vue';
    import RentalExpenseAccountSelect from '../common/rental-expense-account-select.vue';
    import RentalExpenseAssigneeSelect from '../common/rental-expense-assignee-select.vue';
    import PropertiesSearchMultiselect from '../../generic/properties-search-multiselect/properties-search-multiselect.vue';
    import RentalExpenseVendorSelect from '../common/rental-expense-vendor-select.vue';

    export default {
        name: 'RecurringRentalExpenseDetailsModalForm',
        components: {
            BusyModal,
            FormBadgeSelect,
            FormCheckbox,
            FormDatepicker,
            FormInput,
            FormSelect,
            FormTextarea,
            Modal,
            RentalExpenseAccountSelect,
            RentalExpenseAssigneeSelect,
            PropertiesSearchMultiselect,
            RentalExpenseVendorSelect,
        },
        props: {
            modalId: String,
            create: {
                type: Function,
                default: () => Promise.resolve(),
            },
            update: {
                type: Function,
                default: () => Promise.resolve(),
            },
            initialValues: {
                type: Object,
                default: () => ({}),
            },
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            ...mapGetters('workOrders', ['accountExpenses']),
            title() {
                return this.values && this.values.id
                    ? 'Edit Recurring Rental Expense'
                    : 'Create Recurring Rental Expense';
            },
            bymonth: {
                get: function() {
                    if (this.values && this.values.recurrence && !isEmpty(this.values.recurrence.bymonth)) {
                        return this.values.recurrence.bymonth[0];
                    }
                    return 1;
                },
                set: function(bymonth) {
                    if (bymonth && this.values && this.values.recurrence) {
                        this.values.recurrence.bymonth = [bymonth];
                    }
                },
            },
            bymonthDays() {
                // We use year: 1000 since we want to ignore leap years
                return getDaysInMonth({ year: 1000, month: this.bymonth });
            },
            bymonthday: {
                get: function() {
                    if (this.values && this.values.recurrence && !isEmpty(this.values.recurrence.bymonthday)) {
                        return this.values.recurrence.bymonthday[0];
                    }
                    return 1;
                },
                set: function(bymonthday) {
                    if (this.values && this.values.recurrence) {
                        this.values.recurrence.bymonthday = [bymonthday];
                    }
                },
            },
            isRecurrenceFreqWeekly() {
                return !!(
                    this.values &&
                    this.values.recurrence &&
                    this.values.recurrence.freq === RECURRENCE_FREQ_WEEKLY
                );
            },
            isRecurrenceFreqMonthly() {
                return !!(
                    this.values &&
                    this.values.recurrence &&
                    this.values.recurrence.freq === RECURRENCE_FREQ_MONTHLY
                );
            },
            isRecurrenceFreqYearly() {
                return !!(
                    this.values &&
                    this.values.recurrence &&
                    this.values.recurrence.freq === RECURRENCE_FREQ_YEARLY
                );
            },
            freqOptions() {
                const isPlural = !!(this.values && this.values.recurrence && this.values.recurrence.interval > 1);

                return [
                    { value: RECURRENCE_FREQ_DAILY, text: isPlural ? 'days' : 'day' },
                    { value: RECURRENCE_FREQ_WEEKLY, text: isPlural ? 'weeks' : 'week' },
                    { value: RECURRENCE_FREQ_MONTHLY, text: isPlural ? 'months' : 'month' },
                    { value: RECURRENCE_FREQ_YEARLY, text: isPlural ? 'years' : 'year' },
                ];
            },
            yearlyMonthDayOptions() {
                return range(1, this.bymonthDays + 1).map((n) => ({ value: n, text: n }));
            },
            monthDayOptions() {
                return range(1, 32)
                    .map((n) => ({ value: n, text: n }))
                    .concat([{ value: -1, text: 'Last day of the month' }]);
            },
            monthOptions() {
                return Object.entries(RECURRENCE_MONTH_MAPPING).map(([month, monthText]) => ({
                    value: month,
                    text: monthText,
                }));
            },
            weekdayOptions() {
                return Object.entries(RECURRENCE_WEEKDAY_MAPPING).map(([weekday, weekdayText]) => ({
                    value: weekday,
                    text: weekdayText,
                }));
            },
        },
        data() {
            return {
                loading: false,
                properties: [],
                values: null,
            };
        },
        mounted() {
            (async () => {
                this.loading = true;
                try {
                    await Promise.all([this.fetchAccountExpenses(), this.fetchVendors(), this.fetchProperties()]);
                    this.values = this.getInitialValues();
                } finally {
                    this.loading = false;
                }
            })();
        },
        methods: {
            getInitialValues() {
                const getRecurrence = (maybeRecurrence) => {
                    const recurrence = maybeRecurrence || {};
                    return {
                        freq: recurrence.freq || RECURRENCE_FREQ_DAILY,
                        start: recurrence.start
                            ? format(parseISO(recurrence.start), 'yyyy-MM-dd')
                            : format(new Date(), 'yyyy-MM-dd'),
                        until: !!recurrence.until ? format(parseISO(recurrence.until), 'yyyy-MM-dd') : null,
                        interval: recurrence.interval || 1,
                        byday: recurrence.byday || [],
                        bymonth: recurrence.bymonth || [],
                        bymonthday: recurrence.bymonthday || [],
                    };
                };

                return {
                    id: this.initialValues.id || null,
                    amount: this.initialValues.amount || '',
                    account_id:
                        this.initialValues.account_id ||
                        (this.accountExpenses.length > 0 ? this.accountExpenses[0].id : null),
                    vendor_id: this.initialValues.vendor_id || null,
                    assignee: isNil(this.initialValues.assignee) ? 1 : this.initialValues.assignee,
                    description: this.initialValues.description || '',
                    properties: this.initialValues.properties || [],
                    recurrence: getRecurrence(this.initialValues.recurrence),
                };
            },
            getMonthDayOptions(days) {
                return range(1, days + 1)
                    .map((n) => ({ value: n, text: n }))
                    .concat([{ value: -1, text: 'Last day of the month' }]);
            },
            hideModal() {
                this.$bvModal.hide(this.modalId);
            },
            async fetchAccountExpenses() {
                try {
                    await this.$store.dispatch('workOrders/fetchAccountExpenses');
                } catch (err) {
                    this.errorToast({ text: 'Could not load account expenses.' });
                }
            },
            async fetchVendors() {
                try {
                    await this.$store.dispatch('workOrders/fetchVendors');
                } catch (err) {
                    this.errorToast({ text: 'Could not load vendors.' });
                }
            },
            async fetchProperties() {
                try {
                    const response = await API.get(`/app/rental-expense/rental-expense/properties/?with_disabled=true`);
                    this.properties = response.data;
                } catch {
                    this.errorToast({ text: 'Could not load properties.' });
                }
            },
            async save() {
                this.loading = true;

                try {
                    await (this.values.id ? this.update(this.values) : this.create(this.values));
                    this.$emit('success-save');
                    this.$nextTick(() => {
                        this.hideModal();
                    });
                } catch (err) {
                    const { generic, fields } = formatResponseError(err);

                    if (generic) {
                        this.errorToast({ text: generic });
                    }
                    if (!isEmpty(fields)) {
                        this.$refs.form.setErrors(fields);

                        const hasPropertiesError = Object.keys(fields).some((field) => field.startsWith('properties'));
                        if (!fields.hasOwnProperty('properties') && hasPropertiesError) {
                            this.$refs.form.setErrors({ properties: 'Properties are not valid.' });
                        }

                        const hasBymonthdayError = Object.keys(fields).some((field) =>
                            field.startsWith('recurrence.bymonthday')
                        );
                        if (!fields.hasOwnProperty('recurrence.bymonthday') && hasBymonthdayError) {
                            this.$refs.form.setErrors({ 'recurrence.bymonthday': 'Day is not valid.' });
                        }
                    }
                } finally {
                    this.loading = false;
                }
            },
            onModalShown() {
                this.$refs.form.setValues(this.getInitialValues());
                this.$refs.form.validate()
            },
        },
        watch: {
            initialValues() {
                this.values = this.getInitialValues();
            },
            bymonthDays() {
                if (this.bymonthday && this.bymonthday > this.bymonthDays) {
                    this.values.recurrence.bymonthday = [1];
                }
            },
            'values.recurrence.freq': {
                handler(freq) {
                    if (this.values.recurrence.freq === RECURRENCE_FREQ_WEEKLY) {
                        if (isEmpty(this.values.recurrence.byday)) {
                            this.values.recurrence.byday = [RECURRENCE_WEEKDAY_MONDAY];
                        }
                    } else {
                        this.values.recurrence.byday = [];
                    }

                    if (
                        this.values.recurrence.freq === RECURRENCE_FREQ_MONTHLY ||
                        this.values.recurrence.freq === RECURRENCE_FREQ_YEARLY
                    ) {
                        if (isEmpty(this.values.recurrence.bymonthday)) {
                            this.values.recurrence.bymonthday = [1];
                        }

                        if (
                            this.values.recurrence.freq === RECURRENCE_FREQ_YEARLY &&
                            this.values.recurrence.bymonthday[0] === -1
                        ) {
                            this.values.recurrence.bymonthday = [1];
                        }

                        if (this.values.recurrence.freq === RECURRENCE_FREQ_YEARLY) {
                            if (isEmpty(this.values.recurrence.bymonth)) {
                                this.values.recurrence.bymonth = [RECURRENCE_MONTH_JANUARY];
                            }
                        } else {
                            this.values.recurrence.bymonth = [];
                        }
                    } else {
                        this.values.recurrence.bymonth = [];
                        this.values.recurrence.bymonthday = [];
                    }
                },
            },
            values: {
                handler() {
                    this.$refs.form.setValues(this.values);
                },
                deep: true,
            },
        },
    };
</script>

<style lang="scss" scoped>
    .recurring-rental-expenses {
        &__repeat-every {
            display: flex;

            &__interval {
                flex-grow: 1;
            }
        }

        &__yearly-repeat-on {
            display: flex;

            &__bymonthday {
                flex-grow: 1;
            }
        }
    }
</style>
